/**
 * DomainName — IDN-aware domain name display.
 *
 * Renders `unicode_name` (U-label) when the backend provides it AND it differs
 * from `name`; falls back to `name` for pure-ASCII domains. The A-label
 * punycode is exposed via a `title` tooltip ONLY when the two forms differ so
 * screen readers and power-users can inspect the wire form.
 *
 * Rules:
 *   - NEVER decode punycode in the frontend — render the server-provided
 *     `unicode_name` field. If the field is absent or equals `name`, show name.
 *   - No npm dependency on tr46/punycode; this is display-only.
 *
 * @example
 *   // ASCII domain — no tooltip
 *   <DomainName name="example.com" />
 *
 *   // IDN domain — shows "budżetomat.pl", tooltip "xn--budetomat-dcc.pl"
 *   <DomainName name="xn--budetomat-dcc.pl" unicode_name="budżetomat.pl" />
 */

export interface DomainDisplayInput {
  /** A-label / punycode — always present, used as the wire identity. */
  name: string
  /**
   * U-label / Unicode form — optionally populated by the backend.
   * When absent or equal to `name`, `name` is shown with no tooltip.
   */
  unicode_name?: string
}

export interface DomainDisplayResult {
  /** The string to render as visible text. */
  label: string
  /**
   * Punycode tooltip value — only set when `unicode_name` differs from `name`.
   * `undefined` means no `title` attribute should be added.
   */
  title: string | undefined
}

/**
 * Pure helper — derive display label + optional tooltip from a domain pair.
 * Usable both in component renders and in plain string contexts (CSV, aria-labels).
 */
export function domainDisplay({
  name,
  unicode_name,
}: DomainDisplayInput): DomainDisplayResult {
  if (unicode_name && unicode_name !== name) {
    return { label: unicode_name, title: name }
  }
  return { label: name, title: undefined }
}

export interface DomainNameProps extends DomainDisplayInput {
  /** Extra CSS classes applied to the outer `<span>`. */
  className?: string
}

/**
 * DomainName — React component wrapper around `domainDisplay`.
 *
 * Renders a `<span data-testid="domain-name">` so e2e specs can locate it.
 * On list pages multiple spans share the same testid — scope assertions by
 * parent row/container, not by testid alone.
 */
export function DomainName({ name, unicode_name, className }: DomainNameProps) {
  const { label, title } = domainDisplay({ name, unicode_name })
  return (
    <span className={className} title={title} data-testid="domain-name">
      {label}
    </span>
  )
}
